import { peekDraftToken } from '@/onboarding-start/draftToken';
import {
  studioOauthResumeFromSearchParams,
  studioOauthResumePath
} from '@/utils/oauthResume';

export const CODALIO_ATTR_STORAGE_KEY = 'codalio_attr';
export const GTM_SIGN_UP_SENT_KEY = 'codalio_gtm_sign_up_sent';
export const EMAIL_PROVIDER = 'email';

export const ATTRIBUTION_QUERY_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'source',
  'gl',
  'gclid'
];

const toSearchParams = (search) => {
  if (search instanceof URLSearchParams) return search;
  return new URLSearchParams(search ?? '');
};

export const attributionSearchFrom = (search) => {
  const p = toSearchParams(search);
  const out = new URLSearchParams();
  ATTRIBUTION_QUERY_KEYS.forEach((key) => {
    const value = p.get(key);
    if (value) out.set(key, value);
  });
  return out;
};

const hasAttributionParams = (p) =>
  ATTRIBUTION_QUERY_KEYS.some((key) => p.get(key));

/** Persist UTM / gclid / landing labels from the current URL into sessionStorage. */
export const persistSignupAttributionFromSearch = (search) => {
  const p = toSearchParams(search);
  if (!hasAttributionParams(p)) return false;

  const payload = {
    src: p.get('utm_source') || p.get('source') || 'direct',
    med: p.get('utm_medium') || '',
    cmp: p.get('utm_campaign') || '',
    gl: p.get('gl') || p.get('gclid') || '',
    lp: p.get('source') || ''
  };

  try {
    sessionStorage.setItem(CODALIO_ATTR_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    return false;
  }

  return true;
};

export const persistSignupAttributionFromLocation = () => {
  if (typeof window === 'undefined') return false;
  return persistSignupAttributionFromSearch(window.location.search);
};

export const readSignupAttribution = () => {
  try {
    return JSON.parse(sessionStorage.getItem(CODALIO_ATTR_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const readSessionFlag = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSessionFlag = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Private mode / blocked storage must not break auth.
  }
};

export const hasPushedSignupDataLayer = () =>
  readSessionFlag(GTM_SIGN_UP_SENT_KEY) === '1';

export const markSignupDataLayerPushed = () => {
  writeSessionFlag(GTM_SIGN_UP_SENT_KEY, '1');
};

/** Omniauth creates the user and signs them in. */
export const isSsoFirstSession = (user) => {
  if (!user?.id) return false;
  if (!user.provider || user.provider === EMAIL_PROVIDER) return false;

  const count = Number(user.sign_in_count);
  return Number.isFinite(count) && count <= 1;
};

/** Fire the GTM `sign_up` event. Must run before any post-registration redirect. */
export const pushSignupDataLayerEvent = (user) => {
  if (typeof window === 'undefined') return;

  const a = readSignupAttribution();
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'sign_up',
    user_id: user?.id,
    attr_source: a.src || 'direct',
    attr_medium: a.med || '',
    attr_campaign: a.cmp || '',
    attr_landing_page: a.lp || '',
    gl_id: a.gl || ''
  });
  markSignupDataLayerPushed();
};

/**
 * After omniauth returns, the SPA is just a logged-in user — fire once on the first
 * authenticated session.
 */
export const maybePushSignupDataLayerForSsoUser = (user) => {
  if (!isSsoFirstSession(user)) return false;
  if (hasPushedSignupDataLayer()) return false;

  pushSignupDataLayerEvent(user);
  return true;
};

/**
 * PostHog person properties for an authenticated user.
 *
 * Blank values are dropped rather than sent as empty strings: email/password
 * users have no name until they complete their profile, and writing "" would
 * clear a name the server already set from an SSO provider.
 */
export const buildPosthogPersonProperties = (user) => {
  const properties = {
    email: user?.email,
    name: user?.name,
    first_name: user?.first_name,
    last_name: user?.last_name
  };

  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => Boolean(value))
  );
};

export const SIGNUP_SURFACE_SIGNUP_PAGE = 'signup_page';
export const SIGNUP_SURFACE_ONBOARDING_START = 'onboarding_start';
export const SIGNUP_SURFACE_CODALIO_STUDIO = 'codalio_studio';

/** Pending Codalio Studio OAuth authorize path, if any (localStorage or ?redirect_to=). */
export const pendingStudioOauthResume = (searchParams) =>
  studioOauthResumePath() ||
  studioOauthResumeFromSearchParams(searchParams) ||
  null;

// A signup on /start is a /start signup: Studio's sign-in never goes through
// /start, and a leftover Studio resume must not cost it its draft-token join.
export const resolveSignupSurface = (baseSurface, searchParams) =>
  baseSurface !== SIGNUP_SURFACE_ONBOARDING_START &&
  pendingStudioOauthResume(searchParams)
    ? SIGNUP_SURFACE_CODALIO_STUDIO
    : baseSurface;

/**
 * Funnel context for wherever the signup form is rendered.
 *
 * `surface` is the place the form is on, passed by that place. It used to be
 * inferred from a draft token being in storage, but the token outlives the visit
 * by a day, so a later plain `/auth/signup` visit was reported as `/start`.
 */
export const buildSignupPageContext = (surface, searchParams) => {
  const redirect = searchParams?.get?.('redirect');
  let hasDescriptionHandoff = false;
  try {
    hasDescriptionHandoff = Boolean(
      sessionStorage.getItem('codalio-saas-content-handoff')
    );
  } catch {
    /* ignore */
  }

  const sourceSurface = resolveSignupSurface(surface, searchParams);
  const oauthResume =
    sourceSurface === SIGNUP_SURFACE_CODALIO_STUDIO
      ? pendingStudioOauthResume(searchParams)
      : null;

  // The token joins a `/start` signup to the pre-auth funnel (S20). Anywhere
  // else it is a leftover from an earlier visit and joins it to the wrong one.
  const draftToken =
    sourceSurface === SIGNUP_SURFACE_ONBOARDING_START ? peekDraftToken() : null;

  return {
    source_surface: sourceSurface,
    draft_token: draftToken,
    // Only present for a Studio sign-in, so every other signup sends what it always did.
    ...(oauthResume ? { oauth_resume: oauthResume } : {}),
    post_login_redirect: redirect === 'subscription' ? 'subscription' : null,
    has_description_handoff: hasDescriptionHandoff
  };
};

/** Extra omniauth query params when a Studio OAuth flow is waiting in storage. */
export const ssoOauthResumeParams = () => {
  const resume = studioOauthResumePath();
  return resume ? { oauth_resume: resume } : {};
};
